import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { callTransactionSearch, downloadAeropayCsv } from "../../../../../services/aeropay/AeropayController";
import { toAeroPayDateTime } from "../../../../../utils/aeropayFormat";
 
export default function useAeropayTransactions() {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [page, setPage] = useState(0); // UI 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const rows = response?.data ?? [];
  const hasNext = !!response?.hasNext;

  const paginationCount = useMemo(() => {
    const len = rows.length;
    if (!len) return 0;
    if (hasNext) return (page + 1) * rowsPerPage + 1;
    return page * rowsPerPage + len;
  }, [rows.length, hasNext, page, rowsPerPage]);

  const fetchPage = async (nextPage = page, nextRowsPerPage = rowsPerPage) => {
    setLoading(true);
    try {
      const result = await callTransactionSearch({
        searchType: "default",
        search: "",
        page: nextPage + 1, // API 1-based
        perPage: nextRowsPerPage,
        orderBy: "desc",
        sortBy: "startTime",
        dateStart: toAeroPayDateTime(startDate, false),
        dateEnd: toAeroPayDateTime(endDate, true),
      });
      setResponse(result ?? null);
    } catch (err) {
      console.error("Transaction search failed:", err);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const onFilter = async () => {
    if (startDate && endDate && dayjs(startDate).isAfter(endDate)) return;
    setPage(0);
    await fetchPage(0, rowsPerPage);
  };

  const onChangePage = (_e, newPage) => {
    setPage(newPage);
    fetchPage(newPage, rowsPerPage);
  };

  const onChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchPage(0, newSize);
  };

  const onExport = async () => {
    const sd = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
    const ed = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;
    if (!sd || !ed) return;

    setExportLoading(true);
    try {
      const dateStart = toAeroPayDateTime(startDate, false);
      const dateEnd = toAeroPayDateTime(endDate, true);

      const { csv, fileName, success, message } = await downloadAeropayCsv({
        startDate: dateStart,
        endDate: dateEnd,
      });

      if (!success) {
        console.error(message || "Export failed");
        return;
      }

      const blob = new Blob([csv ?? ""], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `aeropay-transactions-${sd}-to-${ed}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  return {
    response,
    rows,
    hasNext,
    loading,
    exportLoading,

    page,
    rowsPerPage,
    paginationCount,

    startDate,
    endDate,
    setStartDate,
    setEndDate,

    fetchPage,
    onFilter,
    onExport,

    onChangePage,
    onChangeRowsPerPage,
  };
}
